/**
 * NOS-248: Maps builder/dashboard change events to CDN purge scope.
 *
 * Pure module — the trigger matrix lives here so tests can pin exactly which
 * change purges which URLs. Chrome-level changes (nav, footer, theme, design
 * tokens, domain) render on every page, so they escalate to an org-wide purge;
 * everything else purges only the affected paths.
 */
import { pagePathForSlug, normalizePublicPath } from "./cachePolicy"

export type PurgeTrigger =
  // Page content
  | { type: "section-save"; pageSlug: string; sectionType?: string }
  | { type: "section-reorder"; pageSlug: string }
  | { type: "section-add"; pageSlug: string }
  | { type: "section-delete"; pageSlug: string }
  | { type: "section-visibility"; pageSlug: string }
  // Page lifecycle
  | { type: "page-publish"; pageSlug: string }
  | { type: "page-unpublish"; pageSlug: string }
  | { type: "page-create"; pageSlug: string }
  | { type: "page-delete"; pageSlug: string }
  | { type: "page-settings-save"; pageSlug: string; previousSlug?: string }
  // Blog
  | { type: "blog-post-save"; postSlug: string }
  | { type: "blog-post-publish"; postSlug: string }
  | { type: "blog-post-unpublish"; postSlug: string }
  | { type: "blog-post-delete"; postSlug: string }
  // Events / campaigns / media
  | { type: "event-save"; eventSlug: string }
  | { type: "event-delete"; eventSlug: string }
  | { type: "campaign-save"; campaignSlug: string }
  | { type: "campaign-delete"; campaignSlug: string }
  | { type: "media-save"; videoSlug: string }
  | { type: "media-delete"; videoSlug: string }
  // Chrome-level: rendered on every public page → org-wide purge
  | { type: "nav-change" }
  | { type: "footer-change" }
  | { type: "theme-change" }
  | { type: "design-tokens-change" }
  | { type: "brand-change" } // fonts, colors, logo, custom CSS, announcement bar
  | { type: "domain-change" } // custom domain attach/verify, subdomain change

export type PurgePlan =
  | { scope: "org" }
  | { scope: "paths"; paths: string[] }

const SITEMAP = "/sitemap.xml"

/** Section types that render in the site chrome rather than a single page. */
const CHROME_SECTION_TYPES = new Set(["footer", "navigation", "nav", "header"])

function paths(...list: string[]): PurgePlan {
  return { scope: "paths", paths: [...new Set(list.map(normalizePublicPath))] }
}

const ORG_WIDE: PurgePlan = { scope: "org" }

export function planPurge(trigger: PurgeTrigger): PurgePlan {
  switch (trigger.type) {
    case "section-save":
      if (trigger.sectionType && CHROME_SECTION_TYPES.has(trigger.sectionType)) {
        return ORG_WIDE
      }
      return paths(pagePathForSlug(trigger.pageSlug))
    case "section-reorder":
    case "section-add":
    case "section-delete":
    case "section-visibility":
      return paths(pagePathForSlug(trigger.pageSlug))

    case "page-publish":
    case "page-unpublish":
    case "page-create":
    case "page-delete":
      return paths(pagePathForSlug(trigger.pageSlug), SITEMAP)
    case "page-settings-save": {
      const affected = [pagePathForSlug(trigger.pageSlug)]
      if (trigger.previousSlug && trigger.previousSlug !== trigger.pageSlug) {
        affected.push(pagePathForSlug(trigger.previousSlug), SITEMAP)
      }
      return paths(...affected)
    }

    case "blog-post-save":
      return paths("/blog", `/blog/${trigger.postSlug}`)
    case "blog-post-publish":
    case "blog-post-unpublish":
    case "blog-post-delete":
      return paths("/blog", `/blog/${trigger.postSlug}`, SITEMAP)

    case "event-save":
      return paths("/events", `/events/${trigger.eventSlug}`, SITEMAP)
    case "event-delete":
      return paths("/events", `/events/${trigger.eventSlug}`, SITEMAP)
    case "campaign-save":
    case "campaign-delete":
      return paths("/campaign", `/campaign/${trigger.campaignSlug}`, SITEMAP)
    case "media-save":
    case "media-delete":
      return paths("/media", `/media/${trigger.videoSlug}`)

    case "nav-change":
    case "footer-change":
    case "theme-change":
    case "design-tokens-change":
    case "brand-change":
    case "domain-change":
      return ORG_WIDE
  }
}
